/**
 * 2026 縣市長選舉開票中心 — GA 追蹤
 *
 * 所有追蹤一律透過本檔推送到 GTM dataLayer，頁面與元件不直接呼叫 `$gtm.push`，
 * 確保欄位格式一致。GTM / GA4 端的設定方式與選後報表拉法見
 * docs/election2026/GA_TRACKING.md
 *
 * ─────────────────────────────────────────────────────────────
 * Page view
 * ─────────────────────────────────────────────────────────────
 *
 * 正式站 GTM（GTM-NF95NZR）的 GA4 設定代碼（G-3NVWZR89V8）**只在 `domMounted`
 * 事件觸發**，不是在頁面載入時自動觸發 —— 這是全站 SPA 換頁計數的做法。
 * 頁面沒有推 `domMounted` 就完全不會有 page view，因此三種頁面都必須在 mounted 呼叫
 * trackPageView。欄位沿用全站慣例（pageUrl / pageTitle / pageCategory）。
 *
 * ─────────────────────────────────────────────────────────────
 * 互動事件
 * ─────────────────────────────────────────────────────────────
 *
 * 統一推送 `event: 'election2026'`，以 electionEvent 區分事件，
 * GTM 只需要一個觸發條件與一個 GA4 事件代碼（事件名稱直接帶 electionEvent）。
 *
 * ⚠️ 每次推送都必須帶齊所有欄位（沒有值就給 null）：
 * GTM 的 dataLayer 變數（第 2 版）會沿用上一次推送的值，
 * 若這次省略 electionCounty，GA 會記到上一個事件的縣市，造成統計錯誤。
 */
import { COUNTY_MAP } from './constants'

/**
 * page view 的 pageCategory。全站其他頁面為主分類（政治／財經…），
 * 選舉頁統一給同一個值，方便在 GTM 端辨識
 */
export const PAGE_CATEGORY = '2026縣市長選舉'

/**
 * 互動事件的 dataLayer event 名稱（GTM 自訂事件觸發條件比對此值）
 */
export const TRACKING_EVENT = 'election2026'

/**
 * GA4 事件參數值上限為 100 字
 */
const LABEL_MAX_LENGTH = 100

/**
 * 推送 page view
 *
 * 需在頁面元件的 mounted 呼叫。SPA 換頁（主頁 → 縣市頁）時新頁面元件會重新 mount，
 * 每次換頁都會各自計一次 page view。
 *
 * @param {object} vm 頁面元件實例
 * @param {string} pageTitle 頁面名稱，送到 GA4 的 title 參數
 */
export function trackPageView(vm, pageTitle) {
  if (!vm.$gtm) {
    return
  }
  // 與全站頁面相同，等 DOM 更新完成後再推送
  vm.$nextTick(() => {
    vm.$gtm.push({
      event: 'domMounted',
      pageUrl: vm.$route.fullPath,
      pageTitle,
      pageCategory: PAGE_CATEGORY
    })
  })
}

/**
 * 推送互動事件
 *
 * 事件清單與各欄位意義見 docs/election2026/GA_TRACKING.md §3。
 *
 * @param {object} vm 元件實例
 * @param {string} name 事件名稱，即 GA4 上看到的事件名，如 'election_pin'
 * @param {object} [detail]
 * @param {string} [detail.county] 操作對象的縣市 slug，轉成中文名稱送出（報表較好讀）
 * @param {string} [detail.label] 補充說明，如排序模式、新聞標題
 */
export function trackEvent(vm, name, detail) {
  if (!vm.$gtm) {
    return
  }
  const settings = detail || {}
  const county = COUNTY_MAP[settings.county]
  const label = settings.label === undefined || settings.label === null
    ? null
    : String(settings.label).slice(0, LABEL_MAX_LENGTH)

  vm.$gtm.push({
    event: TRACKING_EVENT,
    electionEvent: name,
    electionCounty: county ? county.name : null,
    electionLabel: label
  })
}
